import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ClipboardCheck, Download, Loader2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { normalizeCategory } from '../lib/categories';

const formatWeight = (g, weightType) => {
  if (weightType === 'pieces' || weightType === 'count') {
    const val = g % 1 === 0 ? g : g.toFixed(1);
    return `${val} pcs`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)}kg`;
  return `${Math.round(g)}g`;
};

const StockCheckPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedCats, setExpandedCats] = useState(new Set());

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const [recipesRes, catsRes] = await Promise.all([
        axios.get(`${API}/recipes`),
        axios.get(`${API}/categories?type=recipe_category`),
      ]);
      const active = recipesRes.data.filter(r => !r.is_archived);
      setRecipes(active);
      setCategories(catsRes.data);
      setExpandedCats(new Set(catsRes.data.map(c => c.name)));
    } catch (e) {
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  // Group recipes by category (normalize legacy "Chilled" -> "Chilled/Hybrid")
  const recipesByCategory = {};
  const catOrder = ['Chilled/Hybrid', 'Baked Goods', 'Heat to Eat', 'Weekly Specials', 'Snack Pots', 'Add-on'];
  for (const r of recipes) {
    const cat = normalizeCategory(r.category) || 'Other';
    if (!recipesByCategory[cat]) recipesByCategory[cat] = [];
    recipesByCategory[cat].push(r);
  }
  const sortedCategories = Object.keys(recipesByCategory).sort((a, b) => {
    const ia = catOrder.indexOf(a);
    const ib = catOrder.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  const filteredRecipes = searchTerm
    ? recipes.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : null;

  const toggleRecipe = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllInCategory = (catName) => {
    const ids = (recipesByCategory[catName] || []).map(r => r.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  };

  const deselectAllInCategory = (catName) => {
    const ids = new Set((recipesByCategory[catName] || []).map(r => r.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  };

  const allInCatSelected = (catName) => {
    const ids = (recipesByCategory[catName] || []).map(r => r.id);
    return ids.length > 0 && ids.every(id => selectedIds.has(id));
  };

  const toggleCatExpand = (cat) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const generateStockCheck = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one recipe');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/stock-check/generate`, {
        recipe_ids: [...selectedIds],
        base_qty: 5,
      });
      setStockData(res.data);
      toast.success(`Stock check generated: ${res.data.ingredients.length} ingredients`);
    } catch (e) {
      toast.error('Failed to generate stock check');
    } finally {
      setGenerating(false);
    }
  };

  const exportExcel = async () => {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const res = await axios.post(`${API}/stock-check/export`, {
        recipe_ids: [...selectedIds],
        base_qty: 5,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Stock_Check_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (e) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Group stock data by supplier
  const ingredientsBySupplier = useMemo(() => {
    const grouped = {};
    if (stockData) {
      for (const item of stockData.ingredients) {
        const s = item.supplier || 'Unknown';
        if (!grouped[s]) grouped[s] = [];
        grouped[s].push(item);
      }
    }
    return grouped;
  }, [stockData]);

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="stock-check-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Stock Check</h1>
          <p className="text-sm text-gray-500 mt-1">Select meals to generate ingredient stock check list</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={generateStockCheck}
            disabled={selectedIds.size === 0 || generating}
            className="btn-primary"
            data-testid="generate-stock-check-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
            Generate ({selectedIds.size} meals)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Recipe Selection */}
        <div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search recipes..."
                  className="pl-8 h-9 text-sm"
                  data-testid="stock-recipe-search"
                />
              </div>
            </div>

            {filteredRecipes ? (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {filteredRecipes.map(r => (
                  <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm transition-all ${
                    selectedIds.has(r.id) ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-100 hover:bg-gray-50'
                  }`}>
                    <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleRecipe(r.id)} className="scale-75" />
                    <span className="truncate">{r.name}</span>
                    <span className="ml-auto shrink-0 px-1.5 py-0.5 bg-gray-100 text-[9px] font-bold rounded uppercase">{normalizeCategory(r.category)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {sortedCategories.map(cat => {
                  const items = recipesByCategory[cat];
                  const isExpanded = expandedCats.has(cat);
                  const allSelected = allInCatSelected(cat);
                  const selectedCount = items.filter(r => selectedIds.has(r.id)).length;

                  return (
                    <div key={cat} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer" onClick={() => toggleCatExpand(cat)}>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider flex-1">{cat}</span>
                        <span className="text-[10px] text-gray-400">{selectedCount}/{items.length}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); allSelected ? deselectAllInCategory(cat) : selectAllInCategory(cat); }}
                          className="text-[10px] font-bold text-[var(--color-primary)] hover:underline px-2"
                          data-testid={`stock-select-all-${cat.replace(/[^a-zA-Z]/g, '')}`}
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="px-2 py-1">
                          {items.sort((a, b) => a.name.localeCompare(b.name)).map(r => (
                            <label key={r.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm transition-all ${
                              selectedIds.has(r.id) ? 'bg-emerald-50' : 'hover:bg-gray-50'
                            }`}>
                              <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleRecipe(r.id)} className="scale-75" />
                              <span className="truncate">{r.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Stock Check Results */}
        <div>
          {stockData ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Ingredients</div>
                    <div className="text-xl font-bold tabular-nums">{stockData.ingredients.length}</div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Recipes</div>
                    <div className="text-xl font-bold tabular-nums">{stockData.recipe_count}</div>
                  </div>
                </div>
                <Button onClick={exportExcel} disabled={exporting} className="btn-primary text-sm" data-testid="export-stock-check-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export Excel
                </Button>
              </div>

              {Object.entries(ingredientsBySupplier).sort(([a], [b]) => a.localeCompare(b)).map(([supplier, items]) => (
                <div key={supplier} className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{supplier}</div>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Ingredient</th>
                          <th>Total Ingredients</th>
                          <th>Used In</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name)).map((item) => (
                          <tr key={item.ingredient_name}>
                            <td className="font-medium text-sm">{item.ingredient_name}</td>
                            <td className="tabular-nums font-semibold"></td>
                            <td className="text-xs text-gray-500">{item.used_in.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-400">
              <ClipboardCheck className="w-16 h-16 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Select recipes and click "Generate" to create stock check list</p>
              <p className="text-xs mt-1 text-gray-300">Base quantity: 5 portions per recipe</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockCheckPage;
